import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { Toaster } from '@hrms/ui';
import { App } from './App.js';
import { LaunchSplash } from './components/LaunchSplash.js';
import { NativeLinkRouting } from './lib/nativeLinks.js';
import { queryClient } from './lib/query-client.js';
import { tsr } from './lib/api.js';
import { SessionProvider } from './session/SessionProvider.js';
import { SalaryPinHost } from './features/salary-pin/SalaryPinHost.js';
import { applyTheme, getStoredTheme } from './lib/theme.js';
import { registerServiceWorker } from './lib/webpush.js';
import { PHONE_VERIFY_PATH } from './lib/phoneVerifyFrame.js';
import { PhoneVerifyFrame } from './features/phone-verify/PhoneVerifyFrame.js';
import './styles.css';

// index.html's inline script already applied the theme before first paint — this re-applies the
// SAME preference through the one real implementation, so if that duplicated bootstrap logic ever
// drifts, this is what stays correct.
applyTheme(getStoredTheme());

// Registered unconditionally of auth state — see webpush.ts's header. `navigator.serviceWorker.
// ready` (what an eventual push subscribe/permission flow awaits) only resolves once a worker is
// active, so this needs to start as early as possible rather than waiting for AppShell to mount.
void registerServiceWorker();

// Every page is a lazily loaded chunk (routes/pages.ts), and a deploy replaces the bundle: someone
// who opened the app before it and only now opens another page asks for a chunk name that no longer
// exists. Vite reports that as `vite:preloadError`; the fix is the new `index.html`, i.e. a reload.
// Once per minute at most, so a chunk that is genuinely broken shows its error instead of looping.
window.addEventListener('vite:preloadError', (event) => {
  const KEY = 'hrms.chunk-reload-at';
  try {
    const last = Number(sessionStorage.getItem(KEY) ?? 0);
    if (Date.now() - last < 60_000) return;
    sessionStorage.setItem(KEY, String(Date.now()));
  } catch {
    // No sessionStorage (blocked site data): nothing would stop a reload loop, so let the error show.
    return;
  }
  event.preventDefault();
  window.location.reload();
});

const root = createRoot(document.getElementById('root')!);

// The hidden frame a company subdomain sends its SMS code through (lib/phoneVerifyFrame.ts): only
// that, never the app around it — no session refresh, no shell, no redirects inside someone's dialog.
if (window.location.pathname === PHONE_VERIFY_PATH) {
  root.render(
    <StrictMode>
      <PhoneVerifyFrame />
    </StrictMode>,
  );
} else {
  root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <tsr.ReactQueryProvider>
          <BrowserRouter>
            {/* Phone app only: an https://*.peopleorbit.in link from an email opens inside the app. */}
            <NativeLinkRouting />
            <SessionProvider>
              <App />
              {/* Phone app only, once per cold start: an overlay above App, which mounts and loads
               * underneath it — it reads the session only to know when to leave. */}
              <LaunchSplash />
            </SessionProvider>
          </BrowserRouter>
          {/* One instance for the whole app — signed-out pages (login) fire toasts too. Every
           * `toast.*(...)` call anywhere renders here; see packages/ui/src/primitives/Toast.tsx. */}
          <Toaster />
          {/* One salary PIN prompt for the whole app (ADR 0041): every request the API refuses for a
           * missing PIN waits on it, then is sent again. */}
          <SalaryPinHost />
        </tsr.ReactQueryProvider>
      </QueryClientProvider>
    </StrictMode>,
  );
}
